import axios from 'axios';

/** `GET /api/analytics/admin/visits` 응답. 서버가 날짜를 오래된 순으로 0 채워 돌려준다. */
export interface SiteVisitDay {
  date: string;
  views: number;
  uniques: number;
}

export interface SiteVisitPath {
  path: string;
  views: number;
}

export interface SiteVisitReferrer {
  referrer: string;
  views: number;
}

export interface SiteVisitsReport {
  app: string;
  days: SiteVisitDay[];
  totals: { views: number; uniques: number };
  topPaths: SiteVisitPath[];
  topReferrers: SiteVisitReferrer[];
}

export type SiteVisitsRange = 7 | 30 | 90;
export const SITE_VISITS_RANGES: readonly SiteVisitsRange[] = [7, 30, 90];

const asCount = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0
);

const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' ? value as Record<string, unknown> : null
);

const asList = (value: unknown): Record<string, unknown>[] => (
  Array.isArray(value) ? value.map(asRecord).filter((item): item is Record<string, unknown> => item !== null) : []
);

export const normalizeSiteVisitsReport = (payload: unknown, fallbackApp: string): SiteVisitsReport => {
  const record = asRecord(payload) ?? {};
  const totals = asRecord(record['totals']) ?? {};
  const days = asList(record['days'])
    .map((day): SiteVisitDay | null => {
      const date = asText(day['date']);
      return date ? { date, views: asCount(day['views']), uniques: asCount(day['uniques']) } : null;
    })
    .filter((day): day is SiteVisitDay => day !== null);
  const topPaths = asList(record['top_paths'])
    .map((item): SiteVisitPath | null => {
      const path = asText(item['path']);
      return path !== null ? { path, views: asCount(item['views']) } : null;
    })
    .filter((item): item is SiteVisitPath => item !== null);
  const topReferrers = asList(record['top_referrers'])
    .map((item): SiteVisitReferrer | null => {
      const referrer = asText(item['referrer']);
      return referrer !== null ? { referrer, views: asCount(item['views']) } : null;
    })
    .filter((item): item is SiteVisitReferrer => item !== null);

  return {
    app: asText(record['app']) ?? fallbackApp,
    days,
    totals: { views: asCount(totals['views']), uniques: asCount(totals['uniques']) },
    topPaths,
    topReferrers,
  };
};

export interface SiteVisitsClient {
  get: (url: string, config: { headers: Record<string, string>; withCredentials: boolean }) => Promise<{ data: unknown }>;
}

export const buildSiteVisitsUrl = (app: string, days: SiteVisitsRange): string => (
  `/api/analytics/admin/visits?app=${encodeURIComponent(app)}&days=${days}`
);

/** SupportAdminPage 와 같은 상대 경로 + Bearer/XSRF 헤더로 사주 백엔드(fortune.ugot.uk)를 부른다. */
export const fetchSiteVisitsReport = async (
  app: string,
  days: SiteVisitsRange,
  headers: Record<string, string>,
  client: SiteVisitsClient = axios,
): Promise<SiteVisitsReport> => {
  const response = await client.get(buildSiteVisitsUrl(app, days), { headers, withCredentials: true });
  return normalizeSiteVisitsReport(response.data, app);
};

export const formatVisitCount = (value: number): string => value.toLocaleString('ko-KR');

/** 빈 referrer 는 직접 방문(주소창·북마크). 표에 빈 칸이 아니라 이름으로 보이게 한다. */
export const formatReferrer = (referrer: string): string => {
  const trimmed = referrer.trim();
  if (!trimmed) return '(직접 방문)';
  try {
    const url = new URL(trimmed);
    return url.host + (url.pathname !== '/' ? url.pathname : '');
  } catch {
    return trimmed;
  }
};
